import React, { memo, useMemo, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useDropzone } from 'react-dropzone';
import { useTheme, createUseStyles, Section, Text, Modal, Icon, Loader } from '@Sage/hoi-poi-ui';
import { useWeb5Context } from 'lib/Web5Context';
import CarouselFullModal from '../../components/CarouselFullModal';
import Image from './Image';
import ModalHeaderLeft from './ModalHeaderLeft';
import ModalHeaderCenter from './ModalHeaderCenter';
import ModalHeaderRight from './ModalHeaderRight';

import styles from './styles';
const useStyles = createUseStyles(styles, 'UploadImageCarousel');

const MAX_IMAGE_SIZE = 16777216;
const ACCEPTED_IMAGE_EXTENSIONS = /\.(jpe?g|png|gif|webp)$/i;

const isValidGalleryImageFile = (file) =>
    file &&
    file.size <= MAX_IMAGE_SIZE &&
    ((file.type || '').startsWith('image/') || ACCEPTED_IMAGE_EXTENSIONS.test(file.name || ''));

const UploadImageCarousel = ({
    limit,
    onDelete,
    onAdd,
    carouselFullModalClass,
    images = [],
    isLoading = false,
}) => {
    const classes = useStyles();
    const theme = useTheme();
    const { t } = useWeb5Context();
    const [modalIsOpen, setModalIsOpen] = useState(false);
    const [currentImage, setCurrentImage] = useState(null);
    const [deleteImage, setDeleteImage] = useState(null);

    const galleryImages = useMemo(() => images ?? [], [images]);

    const handleDelete = useCallback((imageIndex) => {
        setDeleteImage(imageIndex);
    }, []);

    const handleConfirmDelete = useCallback(() => {
        onDelete(deleteImage);
        setDeleteImage(null);
    }, [deleteImage, onDelete]);

    const handleCancelDelete = useCallback(() => {
        setDeleteImage(null);
    }, []);

    const renderContentLeft = useCallback(
        () => (
            <ModalHeaderLeft
                name={
                    (galleryImages[currentImage] && galleryImages[currentImage].description) || null
                }
            />
        ),
        [currentImage, galleryImages],
    );
    const renderContentCenter = useCallback(
        () => <ModalHeaderCenter index={currentImage} totalImages={galleryImages.length} />,
        [currentImage, galleryImages.length],
    );
    const renderContentRight = useCallback(
        () => <ModalHeaderRight index={currentImage} onDelete={handleDelete} />,
        [currentImage, handleDelete],
    );

    const onDrop = useCallback(
        (acceptedFiles) => {
            if (!acceptedFiles) return;
            if (galleryImages.length + acceptedFiles.length > limit) {
                console.warn(`You can't upload more than ${limit - galleryImages.length} images.`);
                return;
            }
            const validFiles = acceptedFiles.filter((file) => {
                if (!isValidGalleryImageFile(file)) {
                    console.warn('File type not supported');
                    return false;
                }
                return true;
            });

            if (!validFiles.length) return;

            onAdd(validFiles);
        },
        [galleryImages.length, limit, onAdd],
    );

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop,
        accept: {
            'image/jpeg': ['.jpg', '.jpeg'],
            'image/png': ['.png'],
            'image/gif': ['.gif'],
            'image/webp': ['.webp'],
        },
    });

    const updateImageSelected = useCallback((index) => {
        setCurrentImage(index);
    }, []);

    const openViewImage = useCallback((index) => {
        setCurrentImage(index);
        setModalIsOpen(true);
    }, []);

    const closeViewImage = useCallback(() => {
        setModalIsOpen(false);
    }, []);

    const imagesList = useMemo(() => {
        return galleryImages.map((el, i) => (
            <Image
                key={el.id ?? i}
                img={el}
                index={i}
                onDelete={handleDelete}
                onView={openViewImage}
            />
        ));
    }, [openViewImage, galleryImages, handleDelete]);

    const showDropZone = useMemo(
        () => !isLoading && galleryImages.length < limit,
        [galleryImages.length, isLoading, limit],
    );

    const modalOverrides = useMemo(() => ({ confirmButton: { color: 'danger' } }), []);

    const viewableImageSelected = currentImage !== null && currentImage >= 0 ? currentImage : 0;

    return (
        <div className={classes.root}>
            <Section title={t('label_photo_gallery')} className={classes.section}>
                <div className={classes.container}>
                    <div className={classes.carousel}>
                        {isLoading ? <Loader className={classes.loader} /> : imagesList}
                    </div>
                    {showDropZone && (
                        <div className={classes.dropZone} {...getRootProps()}>
                            <input {...getInputProps()} />
                            {isDragActive ? (
                                <div className={classes.dropZoneActive}>
                                    <Text className={classes.dropText}>
                                        {t('cfm_label_drop_to_upload')}
                                    </Text>
                                </div>
                            ) : (
                                <div className={classes.dropZoneInactive}>
                                    <Icon
                                        name="cloudUpload"
                                        className={classes.cloudUploadIcon}
                                        color={theme.colors.utility.textSecondary}
                                    />
                                    <Text className={classes.dropText}>
                                        {t('cfm_label_drop_to_upload')}
                                    </Text>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </Section>
            <CarouselFullModal
                isOpen={modalIsOpen}
                withCloseIcon
                onRequestClose={closeViewImage}
                updateImageSelected={updateImageSelected}
                contentLeft={renderContentLeft}
                contentCenter={renderContentCenter}
                contentRight={renderContentRight}
                images={galleryImages}
                imageSelected={viewableImageSelected}
                className={carouselFullModalClass}
            />
            <Modal
                title={t('title_warning')}
                isOpen={deleteImage !== null}
                size="small"
                onRequestClose={handleCancelDelete}
                onCancel={handleCancelDelete}
                onConfirm={handleConfirmDelete}
                cancelText={t('cfm_action_no')}
                confirmText={t('cfm_action_yes')}
                overlayClassName={classes.deleteModalOverlay}
                overrides={modalOverrides}
            >
                <Text>{t('label_title_delete_registry_modal')}</Text>
            </Modal>
        </div>
    );
};

UploadImageCarousel.propTypes = {
    limit: PropTypes.number.isRequired,
    images: PropTypes.array.isRequired,
    isLoading: PropTypes.bool,
};

export default memo(UploadImageCarousel);
